import { faArrowLeft, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css, styled } from '@mui/material';
import Button from '@mui/material/Button';
import { FooterAction } from '@pages/CreateNewProductPage/styles';
import { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { SimpleSpinner } from 'src/components/Loading/SimpleSpinner';

export function FormFooter({
  buttonDisabled,
  isLoading,
  buttonI18nText,
  backButton,
  previousStepURL,
  nextStepURL,
  nextOnClick,
}: Readonly<{
  buttonDisabled: boolean;
  isLoading?: boolean;
  buttonI18nText: string;
  backButton: boolean;
  previousStepURL?: string;
  nextStepURL?: string;
  nextOnClick?: MouseEventHandler<HTMLButtonElement>;
}>) {
  const { t } = useTranslation('createProduct');

  return (
    <FooterContainer id="create-product-footer" backButton={backButton}>
      {backButton && (
        <BackButton
          variant="contained"
          size="large"
          startIcon={<FontAwesomeIcon icon={faArrowLeft} size="3x" />}
          type="button"
          data-testid="previous-step-button"
          href={previousStepURL}
        >
          {t('previousStep')}
        </BackButton>
      )}
      <FooterAction
        variant="contained"
        size="large"
        endIcon={isLoading ? /* istanbul ignore next */ null : <FontAwesomeIcon icon={faArrowRight} size="3x" />}
        type="submit"
        disabled={buttonDisabled}
        data-testid="next-step-button"
        href={nextStepURL}
        onClick={nextOnClick}
      >
        {isLoading ? /* istanbul ignore next */ <SimpleSpinner /> : t(buttonI18nText)}
      </FooterAction>
    </FooterContainer>
  );
}

const shouldForwardProp = (prop: string) => prop !== 'backButton';
const FooterContainer = styled('section', { shouldForwardProp })<{ backButton: boolean }>`
  width: 100%;
  display: flex;
  justify-content: ${({ backButton }) => (backButton ? 'space-between' : 'flex-end')};
  align-items: center;
  ${({ theme }) => css`
    background-color: ${theme.palette.background.paper};
  `}
`;

const BackButton = styled(Button)`
  text-transform: unset;
  ${({ theme }) => css`
    font-size: ${theme.typography.body2.fontSize};
    background-color: ${theme.palette.secondary.main};

    &:hover {
      background-color: ${theme.palette.secondary.light};
    }
  `};

  ${({ theme }) => theme.breakpoints.up('md')} {
    ${({ theme }) => css`
      font-size: ${theme.typography.body1.fontSize};
      font-weight: ${theme.typography.h1.fontWeight};
    `};
  }

  .MuiButton-startIcon {
    margin-right: 1rem;
  }
` as typeof Button;
